import React, { useEffect, useMemo, useRef, useState } from "react";
import { Expand, X } from "lucide-react";
import type {
  IntellioChartPanel,
  IntellioVisualization3DNode,
  IntellioVisualization3DSpaceModel,
} from "./types.js";

type RendererState = "loading" | "ready" | "unavailable" | "error";
type ThreeModule = typeof import("three");

export interface IntellioVisualization3DSpaceProps {
  space: IntellioVisualization3DSpaceModel;
  fallbackPanels?: IntellioChartPanel[];
  variant?: "inline" | "fullscreen" | "embedded";
  onExpand?: () => void;
  onClose?: () => void;
  className?: string;
}

type SceneHandle = {
  render: () => void;
  dispose: () => void;
};

const parseColor = (
  THREE: ThreeModule,
  value: string | null | undefined,
  fallback: number,
) => {
  try {
    return new THREE.Color(value || fallback);
  } catch {
    return new THREE.Color(fallback);
  }
};

const formatMetricValue = (value: unknown): string => {
  if (value == null) return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number")
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(value);
};

const disposeObject = (scene: InstanceType<ThreeModule["Scene"]>) => {
  scene.traverse((object) => {
    const mesh = object as {
      geometry?: { dispose: () => void };
      material?: { dispose: () => void } | Array<{ dispose: () => void }>;
    };
    mesh.geometry?.dispose();
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach((material) => material.dispose());
    } else {
      mesh.material?.dispose();
    }
  });
};

const createScene = (input: {
  THREE: ThreeModule;
  mount: HTMLDivElement;
  space: IntellioVisualization3DSpaceModel;
  reducedMotion: boolean;
  onHover: (node: IntellioVisualization3DNode | null) => void;
  onSelect: (node: IntellioVisualization3DNode | null) => void;
  setRendererState: (state: RendererState) => void;
}): SceneHandle => {
  const {
    THREE,
    mount,
    space,
    reducedMotion,
    onHover,
    onSelect,
    setRendererState,
  } = input;
  const width = Math.max(mount.clientWidth || 720, 320);
  const height = Math.max(mount.clientHeight || 360, 280);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf8faf9);

  const camera = new THREE.PerspectiveCamera(
    47,
    width / Math.max(height, 1),
    0.1,
    2200,
  );
  camera.position.set(0, 0, 420);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    preserveDrawingBuffer: true,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height, false);
  renderer.domElement.setAttribute("data-testid", "ai-visualization3d-canvas");
  renderer.domElement.setAttribute(
    "aria-label",
    "Admissions Three.js visualization",
  );
  renderer.domElement.className = "block h-full w-full";
  mount.replaceChildren(renderer.domElement);

  scene.add(new THREE.AmbientLight(0xffffff, 1.75));
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.3);
  keyLight.position.set(140, 180, 260);
  scene.add(keyLight);
  const rimLight = new THREE.DirectionalLight(0x88d8d0, 0.8);
  rimLight.position.set(-220, -80, 160);
  scene.add(rimLight);

  const group = new THREE.Group();
  scene.add(group);
  const clusterGroup = new THREE.Group();
  const edgeGroup = new THREE.Group();
  const nodeGroup = new THREE.Group();
  group.add(clusterGroup, edgeGroup, nodeGroup);

  const nodeById = new Map(space.nodes.map((node) => [node.id, node]));
  const meshByNodeId = new Map<string, InstanceType<ThreeModule["Mesh"]>>();
  const pickableMeshes: InstanceType<ThreeModule["Mesh"]>[] = [];

  for (const cluster of space.clusters) {
    const geometry = new THREE.SphereGeometry(cluster.radius, 42, 20);
    const material = new THREE.MeshStandardMaterial({
      color: parseColor(THREE, cluster.color, 0x2697a2),
      transparent: true,
      opacity: 0.08,
      roughness: 0.66,
      metalness: 0.04,
      wireframe: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(cluster.center.x, cluster.center.y, cluster.center.z);
    clusterGroup.add(mesh);
  }

  for (const edge of space.edges) {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    if (!source || !target) continue;
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(
        source.position.x,
        source.position.y,
        source.position.z,
      ),
      new THREE.Vector3(
        target.position.x,
        target.position.y,
        target.position.z,
      ),
    ]);
    const material = new THREE.LineBasicMaterial({
      color: parseColor(THREE, edge.color, 0x94a3b8),
      transparent: true,
      opacity: Math.min(0.72, 0.24 + (edge.weight ?? 1) * 0.16),
    });
    edgeGroup.add(new THREE.Line(geometry, material));
  }

  for (const node of space.nodes) {
    const geometry = new THREE.SphereGeometry(
      Math.max(node.radius, 2.5),
      30,
      18,
    );
    const material = new THREE.MeshStandardMaterial({
      color: parseColor(THREE, node.color, 0x08bfae),
      emissive: parseColor(THREE, node.color, 0x08bfae),
      emissiveIntensity: node.kind.includes("priority") ? 0.18 : 0.08,
      transparent: true,
      opacity: node.opacity ?? 0.92,
      roughness: 0.42,
      metalness: 0.16,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(node.position.x, node.position.y, node.position.z);
    mesh.userData = { nodeId: node.id };
    meshByNodeId.set(node.id, mesh);
    pickableMeshes.push(mesh);
    nodeGroup.add(mesh);
  }

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const dragState = {
    active: false,
    moved: false,
    mode: "rotate" as "rotate" | "pan",
    x: 0,
    y: 0,
  };
  let hoveredNodeId: string | null = null;
  let selectedNodeId: string | null = null;
  let frame = 0;

  const render = () => {
    renderer.render(scene, camera);
  };

  const setMeshFocus = (nodeId: string | null, selected: boolean) => {
    if (!nodeId) return;
    const mesh = meshByNodeId.get(nodeId);
    if (!mesh) return;
    mesh.scale.setScalar(selected ? 1.5 : 1.22);
  };

  const clearMeshFocus = (nodeId: string | null) => {
    if (!nodeId) return;
    const mesh = meshByNodeId.get(nodeId);
    if (!mesh) return;
    mesh.scale.setScalar(1);
  };

  const updateHoverFromEvent = (event: PointerEvent) => {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1;
    pointer.y = -(
      ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2 -
      1
    );
    raycaster.setFromCamera(pointer, camera);
    const [hit] = raycaster.intersectObjects(pickableMeshes, false);
    const nodeId =
      typeof hit?.object.userData?.nodeId === "string"
        ? hit.object.userData.nodeId
        : null;
    if (nodeId === hoveredNodeId) {
      return;
    }
    if (hoveredNodeId !== selectedNodeId) {
      clearMeshFocus(hoveredNodeId);
    }
    hoveredNodeId = nodeId;
    if (hoveredNodeId && hoveredNodeId !== selectedNodeId) {
      setMeshFocus(hoveredNodeId, false);
    }
    onHover(hoveredNodeId ? (nodeById.get(hoveredNodeId) ?? null) : null);
    render();
  };

  const handlePointerDown = (event: PointerEvent) => {
    renderer.domElement.setPointerCapture(event.pointerId);
    dragState.active = true;
    dragState.moved = false;
    dragState.mode = event.shiftKey || event.button === 1 ? "pan" : "rotate";
    dragState.x = event.clientX;
    dragState.y = event.clientY;
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!dragState.active) {
      updateHoverFromEvent(event);
      return;
    }
    const dx = event.clientX - dragState.x;
    const dy = event.clientY - dragState.y;
    dragState.moved = dragState.moved || Math.abs(dx) + Math.abs(dy) > 2;
    dragState.x = event.clientX;
    dragState.y = event.clientY;
    if (dragState.mode === "pan") {
      camera.position.x -= dx * 0.34;
      camera.position.y += dy * 0.34;
    } else {
      group.rotation.y += dx * 0.006;
      group.rotation.x += dy * 0.004;
      group.rotation.x = Math.max(-0.86, Math.min(0.86, group.rotation.x));
    }
    render();
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (renderer.domElement.hasPointerCapture(event.pointerId)) {
      renderer.domElement.releasePointerCapture(event.pointerId);
    }
    if (!dragState.moved) {
      updateHoverFromEvent(event);
      const nextSelectedNodeId = hoveredNodeId;
      if (selectedNodeId && selectedNodeId !== nextSelectedNodeId) {
        clearMeshFocus(selectedNodeId);
      }
      selectedNodeId = nextSelectedNodeId;
      if (selectedNodeId) {
        setMeshFocus(selectedNodeId, true);
      }
      onSelect(selectedNodeId ? (nodeById.get(selectedNodeId) ?? null) : null);
      render();
    }
    dragState.active = false;
  };

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    camera.position.z = Math.max(
      140,
      Math.min(860, camera.position.z + event.deltaY * 0.42),
    );
    render();
  };

  const handlePointerLeave = () => {
    if (hoveredNodeId !== selectedNodeId) {
      clearMeshFocus(hoveredNodeId);
    }
    hoveredNodeId = null;
    onHover(null);
    render();
  };

  const handleResize = () => {
    const nextWidth = Math.max(mount.clientWidth || width, 320);
    const nextHeight = Math.max(mount.clientHeight || height, 280);
    renderer.setSize(nextWidth, nextHeight, false);
    camera.aspect = nextWidth / Math.max(nextHeight, 1);
    camera.updateProjectionMatrix();
    render();
  };

  const animate = () => {
    if (!reducedMotion && !dragState.active) {
      group.rotation.y += 0.0018;
    }
    render();
    if (!reducedMotion) {
      frame = window.requestAnimationFrame(animate);
    }
  };

  renderer.domElement.addEventListener("pointerdown", handlePointerDown);
  renderer.domElement.addEventListener("pointermove", handlePointerMove);
  renderer.domElement.addEventListener("pointerup", handlePointerUp);
  renderer.domElement.addEventListener("pointerleave", handlePointerLeave);
  renderer.domElement.addEventListener("wheel", handleWheel, {
    passive: false,
  });
  const resizeObserver =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(handleResize)
      : null;
  resizeObserver?.observe(mount);

  animate();
  setRendererState("ready");

  return {
    render,
    dispose: () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      resizeObserver?.disconnect();
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener(
        "pointerleave",
        handlePointerLeave,
      );
      renderer.domElement.removeEventListener("wheel", handleWheel);
      disposeObject(scene);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    },
  };
};

const VisualizationFallback: React.FC<{
  panels: IntellioChartPanel[];
  state: RendererState;
}> = ({ panels, state }) => (
  <div className="flex min-h-[280px] flex-col justify-center gap-3 bg-[#F8FAF9] p-5">
    <p className="text-xs font-semibold uppercase text-[#2697A2]">
      {state === "loading"
        ? "Preparing visualization"
        : "Visualization unavailable"}
    </p>
    <div className="grid gap-2 sm:grid-cols-2">
      {panels.slice(0, 2).map((panel) => (
        <div
          key={panel.id}
          className="rounded-md border border-[#D9E7E4] bg-white p-3"
        >
          <p className="text-sm font-semibold text-[#1F2E2B]">{panel.title}</p>
          <p className="mt-1 text-xs text-[#64748B]">
            {panel.data.length} categories / {panel.series.length} series
          </p>
        </div>
      ))}
    </div>
  </div>
);

export function IntellioVisualization3DSpace({
  space,
  fallbackPanels = [],
  variant = "inline",
  onExpand,
  onClose,
  className = "",
}: IntellioVisualization3DSpaceProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [rendererState, setRendererState] = useState<RendererState>("loading");
  const [hoveredNode, setHoveredNode] =
    useState<IntellioVisualization3DNode | null>(null);
  const [selectedNode, setSelectedNode] =
    useState<IntellioVisualization3DNode | null>(space.nodes[0] ?? null);
  const detailNode = selectedNode ?? hoveredNode;
  const isFullscreen = variant === "fullscreen";
  const rootTestId = isFullscreen
    ? "ai-visualization3d-fullscreen"
    : "ai-visualization3d-inline-preview";
  const metricEntries = useMemo(
    () => Object.entries(detailNode?.metrics ?? {}).slice(0, 8),
    [detailNode],
  );

  useEffect(() => {
    setSelectedNode(space.nodes[0] ?? null);
    setHoveredNode(null);
  }, [space]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || typeof window === "undefined") {
      setRendererState("unavailable");
      return undefined;
    }

    let cancelled = false;
    let sceneHandle: SceneHandle | null = null;
    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setRendererState("loading");

    void import("three")
      .then((THREE) => {
        if (cancelled || !mountRef.current) {
          return;
        }
        sceneHandle = createScene({
          THREE,
          mount: mountRef.current,
          space,
          reducedMotion,
          onHover: setHoveredNode,
          onSelect: setSelectedNode,
          setRendererState,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setRendererState("error");
        }
      });

    return () => {
      cancelled = true;
      sceneHandle?.dispose();
    };
  }, [space]);

  return (
    <section
      className={[
        "overflow-hidden rounded-lg border border-[#CDEEE9] bg-white shadow-[0_14px_32px_rgba(15,23,42,0.06)]",
        isFullscreen ? "flex h-full min-h-[620px] flex-col" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={rootTestId}
      data-renderer-state={rendererState}
      data-node-count={space.nodes.length}
      data-cluster-count={space.clusters.length}
      data-edge-count={space.edges.length}
      data-source-route={space.source?.route ?? ""}
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#1F2E2B]">
            {space.title}
          </p>
          <p className="mt-0.5 text-xs text-[#64748B]">
            {space.nodes.length} nodes / {space.clusters.length} clusters /{" "}
            {space.edges.length} links
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onExpand && !isFullscreen ? (
            <button
              type="button"
              onClick={onExpand}
              className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-[#D9E7E4] bg-white text-[#496A65] transition-colors hover:bg-[#F8FAF9] hover:text-[#1F2E2B]"
              aria-label="Expand 3D visualization"
              data-testid="ai-visualization3d-expand"
            >
              <Expand size={15} />
            </button>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-[#D9E7E4] bg-white text-[#496A65] transition-colors hover:bg-[#F8FAF9] hover:text-[#1F2E2B]"
              aria-label="Close 3D visualization"
            >
              <X size={15} />
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={[
          "grid min-h-[360px] flex-1 gap-0",
          isFullscreen
            ? "lg:grid-cols-[minmax(0,1fr)_320px]"
            : "lg:grid-cols-[minmax(0,1fr)_280px]",
        ].join(" ")}
      >
        <div className="relative min-h-[360px] bg-[#F8FAF9]">
          {rendererState === "loading" ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-[#F8FAF9]"
              data-testid="ai-visualization3d-inline-loader"
            >
              <div className="h-8 w-8 rounded-full border-2 border-[#CDEEE9] border-t-[#2697A2]" />
            </div>
          ) : null}
          {rendererState === "unavailable" || rendererState === "error" ? (
            <VisualizationFallback
              panels={fallbackPanels}
              state={rendererState}
            />
          ) : (
            <div ref={mountRef} className="h-full min-h-[360px] w-full" />
          )}
        </div>

        <aside className="border-t border-[#E5E7EB] bg-white p-4 lg:border-l lg:border-t-0">
          <div className="grid grid-cols-2 gap-2">
            {space.legend.slice(0, 6).map((item) => (
              <div
                key={item.id}
                className="flex min-w-0 items-center gap-2 rounded-md border border-[#E5E7EB] px-2 py-1.5"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="truncate text-[11px] font-semibold text-[#404F4D]">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          <div
            className="mt-4 rounded-md border border-[#D9E7E4] bg-[#F8FAF9] p-3"
            data-testid="ai-visualization3d-selected-node"
          >
            <p className="text-xs font-semibold uppercase text-[#2697A2]">
              Selected
            </p>
            <p className="mt-1 text-sm font-semibold text-[#1F2E2B]">
              {detailNode?.label ?? "No node selected"}
            </p>
            <p className="mt-0.5 text-xs text-[#64748B]">
              {detailNode?.kind ?? "node"}
            </p>
            {metricEntries.length > 0 ? (
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {metricEntries.map(([key, value]) => (
                  <div key={key} className="min-w-0">
                    <dt className="truncate font-semibold capitalize text-[#64748B]">
                      {key.replace(/([A-Z])/g, " $1")}
                    </dt>
                    <dd className="truncate text-[#1F2E2B]">
                      {formatMetricValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>

          {space.source ? (
            <div className="mt-3 space-y-1 text-xs text-[#64748B]">
              <p>{space.source.applicantCount} authorized rows in window</p>
              <p>{space.source.totalCount} total dashboard matches</p>
            </div>
          ) : null}
        </aside>
      </div>
    </section>
  );
}
